import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import ExcelJS from "exceljs";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";

/**
 * SalesPivotModule
 * -----------------
 * A self-contained, drag-and-drop pivot table wired to the same
 * `/reports/sales` API used by SalesReport.jsx.
 *
 * Usage:
 *   <SalesPivotModule filters={{ fromDate, toDate, region, shop, orderMethod, platform }} />
 *
 * If `filters` isn't passed, the module shows its own minimal
 * date-range controls and fetches independently.
 *
 * TWO PIVOT VIEWS, both wired to the same live data:
 *  - FLAT  ("Separate columns"): every row-dimension field (Region,
 *    Delivered Date, ...) gets its own column, one row per unique
 *    combination — matches the original behavior.
 *  - GROUPED ("Subtotal name"): row fields nest hierarchically — a bold,
 *    collapsible "Region: X" subtotal row (aggregated over just that
 *    group), with the next row field indented underneath it.
 *
 * A toggle switches the ON-SCREEN table between the two. The Excel export
 * has its OWN two buttons ("Download (Flat)" / "Download (Grouped)") so you
 * can export either format regardless of which one is currently displayed.
 */

// ---- field definitions: dimensions (rows/cols) + measures (values) ----
const FIELD_DEFS = {
  region: { label: "Region", type: "dim" },
  shop: { label: "Shop", type: "dim" },
  date: { label: "Delivered Date", type: "dim" },
  delivery_mode: { label: "Delivery Type", type: "dim" },
  platform: { label: "Platform", type: "dim" },
  order_status: { label: "Order Status", type: "dim" },
  total: { label: "Sales Amount (₹)", type: "measure" },
  qty: { label: "Order Qty", type: "measure" },
  order_count: { label: "Order Count", type: "measure" },
};

// order_delivered_at looks like "Monday 2026-08-10 12:00:00" — same parsing
// logic as SalesReport.jsx's addPivotSummarySheet, kept identical on purpose.
function getDeliveredISO(dateStr) {
  if (!dateStr && dateStr !== 0) return null;
  const value = String(dateStr).trim();
  if (!value || value === "null" || value === "undefined") return null;

  const isoMatch = value.match(/(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];

  const dateMatch = value.match(/(\d{2}[/-]\d{2}[/-]\d{4})/);
  if (dateMatch) {
    const [d, m, y] = dateMatch[1].split(/[/-]/);
    return `${y}-${m}-${d}`;
  }

  const rawDate = new Date(value);
  if (!Number.isNaN(rawDate.getTime())) {
    const y = rawDate.getFullYear();
    const m = String(rawDate.getMonth() + 1).padStart(2, "0");
    const d = String(rawDate.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  const spacedParts = value.split(/\s+/);
  const isoFromParts = spacedParts.find((part) =>
    /^\d{4}-\d{2}-\d{2}$/.test(part),
  );
  return isoFromParts || null;
}

function isoToDMY(iso) {
  if (!iso) return "-";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const AGGS = ["sum", "avg", "count", "min", "max"];

// FIX: previously this had `if (field === "order_count") return records.length;`
// BEFORE the switch, which meant picking sum/avg/min/max on the "Order Count"
// field silently ignored your choice and always showed the same number as
// "count" — the column header would say "(avg)" but the value shown was
// actually the row count, not an average. Now every record just contributes
// a value of 1 for order_count, and the chosen aggregation runs normally, so
// the label and the value always match (sum = row count, avg = 1, etc).
function aggregate(records, agg, field) {
  if (records.length === 0) return null;
  const nums =
    field === "order_count"
      ? records.map(() => 1)
      : records.map((r) => Number(r[field]) || 0);
  switch (agg) {
    case "sum":
      return nums.reduce((a, b) => a + b, 0);
    case "avg":
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    case "count":
      return records.length;
    case "min":
      return Math.min(...nums);
    case "max":
      return Math.max(...nums);
    default:
      return null;
  }
}

// FIX: "count" is always a plain count of orders — never a rupee value, even
// when counting rows of the Sales Amount field. Previously this only looked
// at `field`, so picking "count" on Sales Amount showed e.g. "₹5" for 5
// orders, which is wrong — it should just show 5.
function fmt(n, field, agg) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  if (agg === "count" || field === "order_count") return Math.round(n);
  if (field === "total") return "₹" + Math.round(n).toLocaleString("en-IN");
  return Math.round(n * 100) / 100;
}

function groupBy(records, fields) {
  if (fields.length === 0) return { "": records };
  const groups = {};
  records.forEach((r) => {
    const key = fields.map((f) => r[f]).join(" › ");
    (groups[key] = groups[key] || []).push(r);
  });
  return groups;
}

// ---- key sorting helpers ----
// Row/column group keys are display strings (e.g. "Chennai › 22/05/2026"),
// which sort wrong for dates ("26/10/2024" < "04/07/2026" alphabetically).
// These helpers sort groups using the *underlying* field values instead
// (date_iso for the "date" field, raw value otherwise), while the table
// still displays the original DD/MM/YYYY (or other) label.
function fieldSortValue(record, field) {
  if (field === "date") return record.date_iso || "";
  const v = record[field];
  return v === null || v === undefined ? "" : v;
}

function compareByFields(recA, recB, fields) {
  for (const f of fields) {
    const av = fieldSortValue(recA, f);
    const bv = fieldSortValue(recB, f);
    if (av < bv) return -1;
    if (av > bv) return 1;
  }
  return 0;
}

// ---- hierarchical row tree (GROUPED / "subtotal name" mode) ----
// Builds nested groups: level 0 = rows[0], level 1 = rows[0]+rows[1], etc.
// Each node carries its own slice of records, so subtotal aggregation is
// just cellValue()/aggregate() run over a smaller record set — same math,
// no separate rollup logic to keep in sync.
function buildRowTree(records, rowFields, path = [], depth = 0) {
  if (depth >= rowFields.length) return null;
  const field = rowFields[depth];
  const groups = {};
  const order = [];
  records.forEach((r) => {
    const key = String(r[field]);
    if (!(key in groups)) {
      groups[key] = [];
      order.push(key);
    }
    groups[key].push(r);
  });

  order.sort((a, b) => compareByFields(groups[a][0], groups[b][0], [field]));

  return order.map((key) => {
    const groupRecords = groups[key];
    const nodePath = [...path, key];
    const isLeaf = depth === rowFields.length - 1;
    return {
      key,
      field,
      value: groupRecords[0][field],
      records: groupRecords,
      depth,
      isLeaf,
      pathKey: nodePath.join("§"),
      children: isLeaf
        ? null
        : buildRowTree(groupRecords, rowFields, nodePath, depth + 1),
    };
  });
}

// Flattens the tree into display order. A collapsed group's children (and
// their descendant leaf rows) are simply omitted from the output.
function flattenRowTree(nodes, collapsedKeys, out = []) {
  nodes.forEach((node) => {
    out.push(node);
    if (!node.isLeaf && !collapsedKeys.has(node.pathKey)) {
      flattenRowTree(node.children, collapsedKeys, out);
    }
  });
  return out;
}

function collectGroupKeys(nodes, out = []) {
  nodes.forEach((n) => {
    if (!n.isLeaf) {
      out.push(n.pathKey);
      collectGroupKeys(n.children, out);
    }
  });
  return out;
}

export default function SalesPivotModule({ filters }) {
  const [loading, setLoading] = useState(false);
  const [rawOrders, setRawOrders] = useState([]);
  const [error, setError] = useState("");
  const [showTopLeast, setShowTopLeast] = useState(false);

  // same Redux lists SalesReport.jsx uses to resolve region_id/shop_id → names
  const regions = useSelector((state) => state.counter.allRegionList);
  const allShops = useSelector((state) => state.counter.shopList);

  const getShopName = useCallback(
    (id) => allShops.find((s) => s.shop_details_id === id)?.shop_code || "-",
    [allShops],
  );
  const getRegionName = useCallback(
    (id) => regions.find((r) => r.region_id === id)?.city?.name || "-",
    [regions],
  );

  const loginType = sessionStorage.getItem("loginType") || "admin";
  const loggedInShopId = Number(sessionStorage.getItem("shop_id")) || 0;
  const orderMethods = ["ALL", "Door Step Delivery", "Pick Up"];

  // full filter bar — same fields as SalesReport.jsx. Used unless `filters` prop
  // is supplied by a parent that wants to control filtering itself.
  const [orderMethod, setOrderMethod] = useState("ALL");
  const [fromDate, setFromDate] = useState(filters?.fromDate || "");
  const [toDate, setToDate] = useState(filters?.toDate || "");
  const [region, setRegion] = useState("");
  const [shop, setShop] = useState("");
  const [platform] = useState("ALL");

  const [filteredShops, setFilteredShops] = useState([]);
  const [filteredRegions, setFilteredRegions] = useState([]);

  const isRegionDisabled = loginType === "shop";
  const isShopDisabled = loginType === "shop";

  useEffect(() => {
    if (loginType === "shop" && loggedInShopId) {
      setShop(loggedInShopId);
      const shopDetails = allShops.find(
        (s) => s.shop_details_id === loggedInShopId,
      );
      if (shopDetails && shopDetails.region_id)
        setRegion(shopDetails.region_id);
    }
  }, [loginType, loggedInShopId, allShops]);

  useEffect(() => {
    if (loginType === "shop" && loggedInShopId) {
      const shopDetails = allShops.find(
        (s) => s.shop_details_id === loggedInShopId,
      );
      setFilteredRegions(
        shopDetails?.region_id
          ? regions.filter((r) => r.region_id === shopDetails.region_id)
          : [],
      );
    } else {
      setFilteredRegions(regions);
    }
  }, [loginType, loggedInShopId, regions, allShops]);

  useEffect(() => {
    if (loginType === "shop" && loggedInShopId) {
      const shopDetails = allShops.find(
        (s) => s.shop_details_id === loggedInShopId,
      );
      setFilteredShops(shopDetails ? [shopDetails] : []);
    } else if (region) {
      setFilteredShops(
        allShops.filter((s) => s.region_id === parseInt(region)),
      );
    } else {
      setFilteredShops(allShops);
    }
  }, [loginType, loggedInShopId, region, allShops]);

  const [rows, setRows] = useState(["region"]);
  const [cols, setCols] = useState(["delivery_mode"]);
  const [vals, setVals] = useState([{ field: "total", agg: "sum" }]);

  // "flat"    = separate columns per row-field, one row per combination
  // "grouped" = hierarchical, collapsible subtotal-name rows
  const [viewMode, setViewMode] = useState("flat");

  // ---- fetch live data from the API ----
  const loadData = useCallback(() => {
    setLoading(true);
    setError("");

    let params;
    if (filters) {
      params = { ...filters };
    } else {
      params = {};
      if (orderMethod !== "ALL") params.delivery_mode = orderMethod;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (region) params.region_id = parseInt(region);
      if (shop) params.shop_id = parseInt(shop);
      if (platform !== "ALL") params.platform = platform;
    }

    axios
      .post("/reports/sales", params)
      .then((res) => {
        const orders = res?.data?.data?.data || [];
        setRawOrders(orders);
      })
      .catch(() => {
        setRawOrders([]);
        setError("Couldn't load data from /reports/sales. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [filters, orderMethod, fromDate, toDate, region, shop, platform]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Refresh button = refetch data AND clear the pivot layout back to empty,
  // so the user starts from a blank Rows/Columns/Values state each time.
  const handleRefresh = () => {
    setRows([]);
    setCols([]);
    setVals([]);
    loadData();
  };

  // ---- flatten API orders into pivot-friendly records ----
  // region_name / shop_name don't exist on the raw API response — they must be
  // resolved from region_id / shop_id against the Redux lists, and the id to use
  // depends on delivery_mode (Door Step Delivery nests ids under shop_orders).
  const records = useMemo(() => {
    return rawOrders.map((o) => {
      const isDoorstep = o.delivery_mode === "Door Step Delivery";
      const shopId = isDoorstep ? o.shop_orders?.shop_id : o.shop_id;
      const regionId = isDoorstep ? o.shop_orders?.region_id : o.region_id;

      const productsQty = (o.ordered_products || []).reduce(
        (s, p) => s + Number(p.product_quantity || 0),
        0,
      );
      const addonsQty = (o.ordered_addons || []).reduce(
        (s, a) => s + Number(a.quantity || 0),
        0,
      );

      const deliveredIso = getDeliveredISO(o.order_delivered_at);

      return {
        region: getRegionName(regionId),
        shop: getShopName(shopId),
        date: deliveredIso ? isoToDMY(deliveredIso) : "-",
        date_iso: deliveredIso,
        delivery_mode: o.delivery_mode || "-",
        platform: o.platform || "-",
        order_status: o.order_status || "-",
        total: Number(o.order_overall_totall) || 0,
        qty: productsQty + addonsQty,
        ordered_products: o.ordered_products || [],
      };
    });
  }, [rawOrders, getRegionName, getShopName]);

  // ---- Region → Shop → Date → Top/Least Seller breakdown ----
  // Mirrors Table 4 ("byRegionShopProducts") from SalesReport.jsx's
  // addPivotSummarySheet, so the same rollup is available live in the module.
  const topLeastRows = useMemo(() => {
    const byRegionShopDate = {};

    records.forEach((r) => {
      byRegionShopDate[r.region] = byRegionShopDate[r.region] || {};
      byRegionShopDate[r.region][r.shop] =
        byRegionShopDate[r.region][r.shop] || {};
      byRegionShopDate[r.region][r.shop][r.date] = byRegionShopDate[r.region][
        r.shop
      ][r.date] || { products: {}, date_iso: r.date_iso };

      r.ordered_products.forEach((product) => {
        let details = product.product_details;
        if (Array.isArray(details) && details.length > 0) details = details[0];
        const name = details?.product_name || "Unknown Product";
        const total = Number(product.product_total) || 0;
        const qty = Number(product.product_quantity) || 0;

        const bucket = byRegionShopDate[r.region][r.shop][r.date].products;
        bucket[name] = bucket[name] || { total: 0, quantity: 0 };
        bucket[name].total += total;
        bucket[name].quantity += qty;
      });
    });

    const rowsOut = [];
    Object.keys(byRegionShopDate)
      .sort()
      .forEach((regionName) => {
        Object.keys(byRegionShopDate[regionName])
          .sort()
          .forEach((shopName) => {
            // sort dates by underlying ISO value, not the DD/MM/YYYY label
            Object.keys(byRegionShopDate[regionName][shopName])
              .sort((a, b) => {
                const isoA =
                  byRegionShopDate[regionName][shopName][a].date_iso || "";
                const isoB =
                  byRegionShopDate[regionName][shopName][b].date_iso || "";
                return isoA < isoB ? -1 : isoA > isoB ? 1 : 0;
              })
              .forEach((dateLabel) => {
                const products = Object.entries(
                  byRegionShopDate[regionName][shopName][dateLabel].products,
                );
                if (!products.length) return;
                const sorted = products.sort((a, b) => b[1].total - a[1].total);
                const top = sorted[0];
                const least = sorted[sorted.length - 1];
                rowsOut.push({
                  region: regionName,
                  shop: shopName,
                  date: dateLabel,
                  topSeller: top[0],
                  topQty: top[1].quantity,
                  topAmount: top[1].total,
                  leastSeller: least[0],
                  leastQty: least[1].quantity,
                  leastAmount: least[1].total,
                });
              });
          });
      });
    return rowsOut;
  }, [records]);

  // ---- drag & drop handlers ----
  const usedFields = () =>
    new Set([...rows, ...cols, ...vals.map((v) => v.field)]);

  // moveField handles three cases with one function:
  //  1. dragging a field in from the FIELDS list into a zone (append)
  //  2. dragging a field from one zone into another zone (move + append)
  //  3. dragging a chip onto another chip WITHIN THE SAME ZONE (reorder)
  // targetField is the field of the chip that was dropped on, or null/undefined
  // when dropped on empty zone space (meaning "put it at the end").
  const moveField = (zone, field, targetField) => {
    const def = FIELD_DEFS[field];
    if (!def) return;
    if (zone === "vals" && def.type !== "measure") return;
    if ((zone === "rows" || zone === "cols") && def.type !== "dim") return;

    // Compute the insert index BEFORE removal, based on the zone's current order.
    const currentArr =
      zone === "vals"
        ? vals.map((v) => v.field)
        : zone === "rows"
          ? rows
          : cols;
    const fromIndex = currentArr.indexOf(field);
    let targetIndex =
      targetField != null ? currentArr.indexOf(targetField) : currentArr.length;
    if (targetIndex === -1) targetIndex = currentArr.length;
    // if the dragged field was already before the drop target in this same
    // zone, removing it first shifts everything after it back by one
    if (fromIndex !== -1 && fromIndex < targetIndex) targetIndex -= 1;

    setRows((r) => r.filter((f) => f !== field));
    setCols((c) => c.filter((f) => f !== field));
    setVals((v) => v.filter((x) => x.field !== field));

    const clampIndex = (len) => Math.min(Math.max(targetIndex, 0), len);

    if (zone === "vals") {
      setVals((v) => {
        const arr = [...v];
        arr.splice(clampIndex(arr.length), 0, { field, agg: "sum" });
        return arr;
      });
    } else if (zone === "rows") {
      setRows((r) => {
        const arr = [...r];
        arr.splice(clampIndex(arr.length), 0, field);
        return arr;
      });
    } else if (zone === "cols") {
      setCols((c) => {
        const arr = [...c];
        arr.splice(clampIndex(arr.length), 0, field);
        return arr;
      });
    }
  };

  const removeField = (zone, field) => {
    if (zone === "vals") setVals((v) => v.filter((x) => x.field !== field));
    else if (zone === "rows") setRows((r) => r.filter((f) => f !== field));
    else if (zone === "cols") setCols((c) => c.filter((f) => f !== field));
  };

  // ---- FLAT pivot computation (separate columns per row-field) ----
  const rowGroups = useMemo(() => groupBy(records, rows), [records, rows]);

  // Sort row keys using the underlying field values (so "date" sorts
  // chronologically via date_iso instead of alphabetically on DD/MM/YYYY).
  const rowKeys = useMemo(() => {
    return Object.keys(rowGroups).sort((a, b) =>
      compareByFields(rowGroups[a][0], rowGroups[b][0], rows),
    );
  }, [rowGroups, rows]);

  // ---- GROUPED pivot computation (hierarchical subtotal rows) ----
  const [collapsedKeys, setCollapsedKeys] = useState(new Set());

  // reset collapse state whenever the Rows layout itself changes — stale
  // pathKeys from a previous field combination shouldn't silently persist
  useEffect(() => {
    setCollapsedKeys(new Set());
  }, [rows]);

  const rowTree = useMemo(
    () => (rows.length > 0 ? buildRowTree(records, rows) : null),
    [records, rows],
  );

  const flatRowsGrouped = useMemo(
    () => (rowTree ? flattenRowTree(rowTree, collapsedKeys) : []),
    [rowTree, collapsedKeys],
  );

  const toggleCollapse = (pathKey) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(pathKey)) next.delete(pathKey);
      else next.add(pathKey);
      return next;
    });
  };

  const handleExpandAll = () => setCollapsedKeys(new Set());
  const handleCollapseAll = () =>
    setCollapsedKeys(new Set(collectGroupKeys(rowTree || [])));

  // columns stay flat in BOTH modes by design (Delivery Type / Platform etc.
  // don't need a nested hierarchy the way Region → Shop does)
  const colGroupsForSort = useMemo(
    () => groupBy(records, cols),
    [records, cols],
  );

  const colKeys = useMemo(() => {
    if (cols.length === 0) return [""];
    return Object.keys(colGroupsForSort).sort((a, b) =>
      compareByFields(colGroupsForSort[a][0], colGroupsForSort[b][0], cols),
    );
  }, [colGroupsForSort, cols]);

  const displayColKeys = cols.length > 0 ? [...colKeys, "__total__"] : [""];

  const cellValue = (recordsForRow, colKey, field, agg) => {
    let subset;
    if (colKey === "__total__") subset = recordsForRow;
    else if (cols.length > 0)
      subset = recordsForRow.filter(
        (r) => cols.map((f) => r[f]).join(" › ") === colKey,
      );
    else subset = recordsForRow;
    return aggregate(subset, agg, field);
  };

  // Excel number format per measure — depends on BOTH the field and the
  // aggregation. "count" is always a plain count of orders, even when
  // counting rows of the Sales Amount field, so it must never get "₹".
  const excelNumFmt = (field, agg) => {
    if (agg === "count" || field === "order_count") return "0";
    if (field === "total") return '"₹"#,##0';
    return "0.00";
  };

  // Writes a measure value into a cell as a real number (with numFmt) rather
  // than a formatted display string — fixes Excel's green "number stored as
  // text" corner flag that shows up when you write e.g. "₹1,338" as text.
  const writeMeasureCell = (cell, rawValue, field, agg) => {
    if (rawValue === null || rawValue === undefined || Number.isNaN(rawValue)) {
      cell.value = "—";
    } else {
      cell.value = rawValue;
      cell.numFmt = excelNumFmt(field, agg);
    }
  };

  const buildHeaderRow = (sheet, HEADER_FILL, BORDER, rowFieldCount) => {
    rows.forEach((f, i) => {
      const cell = sheet.getCell(1, i + 1);
      cell.value = FIELD_DEFS[f].label;
      cell.font = { bold: true };
      cell.fill = HEADER_FILL;
      cell.border = BORDER;
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });
    if (rows.length === 0) {
      const cell = sheet.getCell(1, 1);
      cell.value = "";
      cell.fill = HEADER_FILL;
      cell.border = BORDER;
      cell.alignment = { horizontal: "center", vertical: "middle" };
    }

    let colIdx = rowFieldCount + 1;
    displayColKeys.forEach((ck) => {
      vals.forEach((v) => {
        const cell = sheet.getCell(1, colIdx);
        const label = colHeaderLabel(ck, v, vals);
        cell.value = label;
        cell.font = { bold: true };
        cell.fill = HEADER_FILL;
        cell.border = BORDER;
        cell.alignment = { horizontal: "center", vertical: "middle" };
        colIdx += 1;
      });
    });
    return colIdx;
  };

  const buildGrandTotalRow = (sheet, HEADER_FILL, BORDER, rowFieldCount, r) => {
    sheet.mergeCells(r, 1, r, rowFieldCount);
    sheet.getCell(r, 1).value = "Grand Total";
    sheet.getCell(r, 1).font = { bold: true };
    sheet.getCell(r, 1).fill = HEADER_FILL;
    sheet.getCell(r, 1).border = BORDER;
    sheet.getCell(r, 1).alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    for (let i = 1; i <= rowFieldCount; i++) {
      sheet.getCell(r, i).border = BORDER;
      sheet.getCell(r, i).fill = HEADER_FILL;
      sheet.getCell(r, i).alignment = {
        horizontal: "center",
        vertical: "middle",
      };
    }
    let c = rowFieldCount + 1;
    displayColKeys.forEach((ck) => {
      vals.forEach((v) => {
        const cell = sheet.getCell(r, c);
        writeMeasureCell(
          cell,
          cellValue(records, ck, v.field, v.agg),
          v.field,
          v.agg,
        );
        cell.font = { bold: true };
        cell.fill = HEADER_FILL;
        cell.border = BORDER;
        cell.alignment = { horizontal: "center", vertical: "middle" };
        c += 1;
      });
    });
  };

  const addTopLeastSheet = (workbook, HEADER_FILL, BORDER) => {
    if (topLeastRows.length === 0) return;
    const t4Sheet = workbook.addWorksheet("Top & Least Sellers");
    const headers = [
      "Region",
      "Shop",
      "Date",
      "Top Seller",
      "Top Qty",
      "Top Amount",
      "Least Seller",
      "Least Qty",
      "Least Amount",
    ];
    headers.forEach((h, i) => {
      const cell = t4Sheet.getCell(1, i + 1);
      cell.value = h;
      cell.font = { bold: true };
      cell.fill = HEADER_FILL;
      cell.border = BORDER;
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    topLeastRows.forEach((row, i) => {
      const r2 = i + 2;
      const rowValues = [
        row.region,
        row.shop,
        row.date,
        row.topSeller,
        row.topQty,
        Math.round(row.topAmount),
        row.leastSeller,
        row.leastQty,
        Math.round(row.leastAmount),
      ];
      rowValues.forEach((val, ci) => {
        const cell = t4Sheet.getCell(r2, ci + 1);
        cell.value = val;
        cell.border = BORDER;
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
    });

    [26, 22, 14, 26, 12, 14, 26, 12, 14].forEach(
      (w, i) => (t4Sheet.getColumn(i + 1).width = w),
    );
  };

  const downloadWorkbook = async (workbook, suffix) => {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Sales_Pivot_${suffix}_${new Date().toISOString().split("T")[0]}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ---- Excel export: FLAT (separate columns, one row per combination) ----
  const handleExportFlat = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Pivot");
    const HEADER_FILL = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD9E1F2" },
    };
    const BORDER = {
      top: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
      bottom: { style: "thin" },
    };

    const rowFieldCount = Math.max(rows.length, 1);
    const colIdx = buildHeaderRow(sheet, HEADER_FILL, BORDER, rowFieldCount);

    // body rows — one value per row-dimension field, in separate cells
    let r = 2;
    rowKeys.forEach((rk) => {
      const sample = rowGroups[rk][0];
      if (rows.length > 0) {
        rows.forEach((f, i) => {
          const cell = sheet.getCell(r, i + 1);
          cell.value = sample?.[f] ?? "-";
          cell.border = BORDER;
          cell.alignment = { horizontal: "center", vertical: "middle" };
        });
      } else {
        sheet.getCell(r, 1).value = rk;
        sheet.getCell(r, 1).border = BORDER;
        sheet.getCell(r, 1).alignment = {
          horizontal: "center",
          vertical: "middle",
        };
      }

      let c = rowFieldCount + 1;
      const recordsForRow = rowGroups[rk];
      displayColKeys.forEach((ck) => {
        vals.forEach((v) => {
          const cell = sheet.getCell(r, c);
          writeMeasureCell(
            cell,
            cellValue(recordsForRow, ck, v.field, v.agg),
            v.field,
            v.agg,
          );
          cell.border = BORDER;
          cell.alignment = { horizontal: "center", vertical: "middle" };
          c += 1;
        });
      });
      r += 1;
    });

    buildGrandTotalRow(sheet, HEADER_FILL, BORDER, rowFieldCount, r);

    for (let i = 1; i <= rowFieldCount; i++) sheet.getColumn(i).width = 20;
    for (let i = rowFieldCount + 1; i < colIdx; i++)
      sheet.getColumn(i).width = 22;

    addTopLeastSheet(workbook, HEADER_FILL, BORDER);
    await downloadWorkbook(workbook, "Flat");
  };

  // ---- Excel export: GROUPED (hierarchical subtotal-name rows) ----
  const handleExportGrouped = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Pivot");
    const HEADER_FILL = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD9E1F2" },
    };
    const SUBTOTAL_FILL = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFEFF3F0" },
    };
    const BORDER = {
      top: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
      bottom: { style: "thin" },
    };

    const rowFieldCount = Math.max(rows.length, 1);
    const colIdx = buildHeaderRow(sheet, HEADER_FILL, BORDER, rowFieldCount);

    let r = 2;

    const writeLeafRow = (node) => {
      const sample = node.records[0];
      rows.forEach((f, i) => {
        const cell = sheet.getCell(r, i + 1);
        cell.value = sample?.[f] ?? "-";
        cell.border = BORDER;
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
      let c = rowFieldCount + 1;
      displayColKeys.forEach((ck) => {
        vals.forEach((v) => {
          const cell = sheet.getCell(r, c);
          writeMeasureCell(
            cell,
            cellValue(node.records, ck, v.field, v.agg),
            v.field,
            v.agg,
          );
          cell.border = BORDER;
          cell.alignment = { horizontal: "center", vertical: "middle" };
          c += 1;
        });
      });
      sheet.getRow(r).outlineLevel = node.depth;
      r += 1;
    };

    const writeSubtotalRow = (node) => {
      const startCol = node.depth + 1;

      sheet.mergeCells(r, startCol, r, rowFieldCount);
      const labelCell = sheet.getCell(r, startCol);
      labelCell.value = `${FIELD_DEFS[node.field].label}: ${node.value ?? "-"}`;
      labelCell.font = { bold: true };
      labelCell.fill = SUBTOTAL_FILL;
      labelCell.border = BORDER;
      labelCell.alignment = {
        horizontal: "left",
        vertical: "middle",
        indent: node.depth,
      };

      for (let i = 1; i < startCol; i++) {
        const cell = sheet.getCell(r, i);
        cell.fill = SUBTOTAL_FILL;
        cell.border = BORDER;
      }

      let c = rowFieldCount + 1;
      displayColKeys.forEach((ck) => {
        vals.forEach((v) => {
          const cell = sheet.getCell(r, c);
          writeMeasureCell(
            cell,
            cellValue(node.records, ck, v.field, v.agg),
            v.field,
            v.agg,
          );
          cell.font = { bold: true };
          cell.fill = SUBTOTAL_FILL;
          cell.border = BORDER;
          cell.alignment = { horizontal: "center", vertical: "middle" };
          c += 1;
        });
      });
      sheet.getRow(r).outlineLevel = node.depth;
      r += 1;
    };

    const writeNodes = (nodes) => {
      nodes.forEach((node) => {
        if (node.isLeaf) writeLeafRow(node);
        else {
          writeSubtotalRow(node);
          writeNodes(node.children);
        }
      });
    };

    writeNodes(rowTree || []);

    // lets Excel's native +/- outline buttons collapse subtotal groups,
    // matching the on-screen collapse/expand
    sheet.properties.outlineProperties = {
      summaryBelow: false,
      summaryRight: false,
    };

    buildGrandTotalRow(sheet, HEADER_FILL, BORDER, rowFieldCount, r);

    for (let i = 1; i <= rowFieldCount; i++) sheet.getColumn(i).width = 20;
    for (let i = rowFieldCount + 1; i < colIdx; i++)
      sheet.getColumn(i).width = 22;

    addTopLeastSheet(workbook, HEADER_FILL, BORDER);
    await downloadWorkbook(workbook, "Grouped");
  };

  const availableFields = Object.keys(FIELD_DEFS).filter(
    (f) => !usedFields().has(f),
  );

  return (
    <Box
      className="sales-pivot-page"
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div>
        <h4>Sales Report</h4>
      </div>
      <Card
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <CardContent
          sx={{
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            overflowX: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
              >
                Refresh
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleExportFlat}
                disabled={loading || records.length === 0}
              >
                Download (Flat)
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleExportGrouped}
                disabled={loading || records.length === 0}
              >
                Download (Grouped)
              </Button>
              {viewMode === "grouped" && rows.length > 1 && (
                <>
                  <Button size="small" onClick={handleExpandAll}>
                    Expand all
                  </Button>
                  <Button size="small" onClick={handleCollapseAll}>
                    Collapse all
                  </Button>
                </>
              )}
              
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <ToggleButtonGroup
                size="small"
                value={viewMode}
                exclusive
                onChange={(e, v) => v && setViewMode(v)}
              >
                <ToggleButton value="flat">Separate columns</ToggleButton>
                <ToggleButton value="grouped">Subtotal name</ToggleButton>
              </ToggleButtonGroup>

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showTopLeast}
                    onChange={(e) => setShowTopLeast(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                    Top & Least Sellers
                  </Typography>
                }
              />

              <Typography variant="h6">Sales Pivot</Typography>
              {rawOrders.length > 0 && (
                <Chip label={`${rawOrders.length} orders`} color="success" size="small" />
              )}
            </Box>
            </Box>
          </Box>

          {!filters && (
            <Grid
              container
              spacing={2}
              sx={{ mb: 2, width: "100%", margin: 0 }}
              alignItems="center"
            >
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Delivery Type</InputLabel>
                  <Select
                    value={orderMethod}
                    label="Delivery Type"
                    onChange={(e) => setOrderMethod(e.target.value)}
                  >
                    {orderMethods.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2.5}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From Date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2.5}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To Date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={1.8}>
                <FormControl fullWidth size="small" disabled={isRegionDisabled}>
                  <InputLabel>ALL Region</InputLabel>
                  <Select
                    value={region}
                    label="Region"
                    onChange={(e) => {
                      setRegion(e.target.value);
                      if (loginType !== "shop") setShop("");
                    }}
                  >
                    <MenuItem value="">All Regions</MenuItem>
                    {filteredRegions?.map((r) => (
                      <MenuItem key={r.region_id} value={r.region_id}>
                        {r.city?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={1.8}>
                <FormControl fullWidth size="small" disabled={isShopDisabled}>
                  <InputLabel>ALL Shop</InputLabel>
                  <Select
                    value={shop}
                    label="Shop"
                    onChange={(e) => setShop(e.target.value)}
                  >
                    <MenuItem value="">All Shops</MenuItem>
                    {filteredShops.map((s) => (
                      <MenuItem
                        key={s.shop_details_id}
                        value={s.shop_details_id}
                      >
                        {s.shop_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          <Grid
            container
            spacing={0}
            sx={{
              minWidth: 0,
              width: "100%",
              margin: 0,
              display: "flex",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              alignItems: "start",
              boxSizing: "border-box",
              marginTop:2,
            }}
          >
            <Grid
              item
              xs={12}
              sm={4}
              sx={{
                minWidth: 0,
                flex: { xs: "0 0 100%", sm: "0 0 33.333333%" },
                maxWidth: { xs: "100%", sm: "33.333333%" },
                width: { xs: "100%", sm: "33.333333%" },
                flexShrink: 1,
                boxSizing: "border-box",
                padding: "0px 10px",
              }}
            >
              <FieldPanel
                availableFields={availableFields}
                rows={rows}
                cols={cols}
                vals={vals}
                onMoveField={moveField}
                onRemove={removeField}
                onAggChange={(field, agg) =>
                  setVals((v) =>
                    v.map((x) => (x.field === field ? { ...x, agg } : x)),
                  )
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={8}
              sx={{
                minWidth: 0,
                flex: { xs: "0 0 100%", sm: "1 1 0%" },
                maxWidth: { xs: "100%", sm: "66.666667%" },
                width: { xs: "100%", sm: 0 },
                flexShrink: 1,
                overflowX: "hidden",
                boxSizing: "border-box",
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : rows.length === 0 || vals.length === 0 ? (
                <Box
                  sx={{ p: 6, textAlign: "center", color: "text.secondary" }}
                >
                  Drag a field into <b>Rows</b> and a measure into <b>Values</b>{" "}
                  to build the pivot.
                </Box>
              ) : viewMode === "flat" ? (
                <FlatPivotTable
                  rows={rows}
                  displayColKeys={displayColKeys}
                  cols={cols}
                  vals={vals}
                  rowKeys={rowKeys}
                  rowGroups={rowGroups}
                  records={records}
                  cellValue={cellValue}
                />
              ) : (
                <GroupedPivotTable
                  rows={rows}
                  displayColKeys={displayColKeys}
                  cols={cols}
                  vals={vals}
                  flatRows={flatRowsGrouped}
                  records={records}
                  cellValue={cellValue}
                  collapsedKeys={collapsedKeys}
                  onToggle={toggleCollapse}
                />
              )}

              {/* {!loading &&
                rows.length > 0 &&
                vals.length > 0 &&
                topLeastRows.length > 0 && (
                  <Box sx={{ mt: 3, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Top & Least Sellers by Region / Shop / Date
                    </Typography>
                    <TopLeastTable rows={topLeastRows} />
                  </Box>
                )} */}
            </Grid>
          </Grid>
          {!loading &&
            showTopLeast &&
            rows.length > 0 &&
            vals.length > 0 &&
            topLeastRows.length > 0 && (
              <Box
                sx={{
                  mt: 3,
                  width: "100%",
                  minWidth: 0,
                  maxWidth: "100%",
                  overflow: "hidden",
                  boxSizing: "border-box",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Top & Least Sellers by Region / Shop / Date
                </Typography>
                <TopLeastTable rows={topLeastRows} />
              </Box>
            )}
        </CardContent>
      </Card>
    </Box>
  );
}

// ---- left panel: field list + drop zones ----
// Chips inside ROWS / COLUMNS / VALUES can be dragged and dropped ONTO ANOTHER
// CHIP in the same zone to reorder them — that reorder is what drives the
// nesting order (Rows) and left-to-right column order (Columns/Values) in
// the pivot table and Excel export below, for BOTH view modes.
function FieldPanel({
  availableFields,
  rows,
  cols,
  vals,
  onMoveField,
  onRemove,
  onAggChange,
}) {
  const [dragOverKey, setDragOverKey] = useState(null); // e.g. "rows:date" — for a drop-target highlight

  // background of the zone box itself (dropping on empty space = append to end)
  const zoneProps = (zone) => ({
    onDragOver: (e) => e.preventDefault(),
    onDrop: (e) => {
      e.preventDefault();
      const field = e.dataTransfer.getData("text/plain");
      onMoveField(zone, field, null);
      setDragOverKey(null);
    },
  });

  // dropping directly on a chip = insert next to that chip (reorder)
  const chipDropProps = (zone, targetField) => ({
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onDragEnter: () => setDragOverKey(`${zone}:${targetField}`),
    onDragLeave: () =>
      setDragOverKey((k) => (k === `${zone}:${targetField}` ? null : k)),
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      const field = e.dataTransfer.getData("text/plain");
      onMoveField(zone, field, targetField);
      setDragOverKey(null);
    },
  });

  const renderChip = (field, zone) => (
    <Chip
      key={field}
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/plain", field)}
      {...chipDropProps(zone, field)}
      label={
        zone === "vals" ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {fieldLabelForAgg(
              field,
              vals.find((v) => v.field === field)?.agg || "sum",
            )}
            <Select
              size="small"
              variant="standard"
              value={vals.find((v) => v.field === field)?.agg || "sum"}
              onChange={(e) => onAggChange(field, e.target.value)}
              sx={{ fontSize: 12 }}
            >
              {AGGS.map((a) => (
                <MenuItem key={a} value={a}>
                  {a}
                </MenuItem>
              ))}
            </Select>
          </Box>
        ) : (
          FIELD_DEFS[field].label
        )
      }
      onDelete={() => onRemove(zone, field)}
      size="small"
      sx={{
        mb: 0.5,
        mr: 0.5,
        cursor: "grab",
        outline:
          dragOverKey === `${zone}:${field}` ? "2px solid #4a6156" : "none",
        outlineOffset: 1,
      }}
    />
  );

  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        FIELDS
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", mb: 2, mt: 0.5 }}>
        {availableFields.map((f) => (
          <Chip
            key={f}
            label={FIELD_DEFS[f].label}
            draggable
            onDragStart={(e) => e.dataTransfer.setData("text/plain", f)}
            size="small"
            variant="outlined"
            sx={{
              mb: 0.5,
              mr: 0.5,
              cursor: "grab",
              borderLeft:
                FIELD_DEFS[f].type === "measure"
                  ? "3px solid #b8862f"
                  : "3px solid #4a6156",
            }}
          />
        ))}
      </Box>

      {["rows", "cols", "vals"].map((zone) => {
        const fieldsInZone =
          zone === "rows"
            ? rows
            : zone === "cols"
              ? cols
              : vals.map((v) => v.field);
        return (
          <Box key={zone} sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {zone === "rows"
                ? "ROWS"
                : zone === "cols"
                  ? "COLUMNS"
                  : "VALUES"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", fontSize: 6, mb: 0.25 }}
            >
              {zone === "rows"
                ? 'order here sets subtotal nesting in "Subtotal name" view (1st field = outermost group)'
                : "drag a chip onto another chip here to reorder"}
            </Typography>
            <Box
              {...zoneProps(zone)}
              sx={{
                minHeight: 44,
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 1,
                p: 1,
                mt: 0.5,
              }}
            >
              {fieldsInZone.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  drop a field here
                </Typography>
              ) : (
                fieldsInZone.map((f) => renderChip(f, zone))
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
function fieldLabelForAgg(field, agg) {
  const label = FIELD_DEFS[field].label;
  return agg === "count" ? label.replace(/\s*\(₹\)/, "") : label;
}
function colHeaderLabel(ck, v, vals) {
  const measureLabel = fieldLabelForAgg(v.field, v.agg);
  if (ck === "__total__") {
    return vals.length > 1 ? `Total - ${measureLabel}` : "Total";
  }
  if (!ck) return measureLabel;
  return vals.length > 1 ? `${ck} - ${measureLabel}` : ck;
}
// ---- FLAT view: row dimensions render as SEPARATE columns ----
// (Region | Delivered Date | ...) instead of one merged text column —
// one row per unique combination, no subtotals.
function FlatPivotTable({
  rows,
  displayColKeys,
  cols,
  vals,
  rowKeys,
  rowGroups,
  records,
  cellValue,
}) {
  const rowFieldCount = Math.max(rows.length, 1);
  const s = useTableStyles();

  return (
    <Box
      sx={{
        overflowX: "scroll",
        overflowY: "auto",
        maxHeight: 520,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overscrollBehaviorX: "contain",
        scrollbarGutter: "stable",
        pb: 1,
        "&::-webkit-scrollbar": { height: 8, width: 8 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#b8c0c8",
          borderRadius: 4,
        },
      }}
    >
      <table
        style={{
          borderCollapse: "separate",
          borderSpacing: 0,
          width: "max-content",
          minWidth: "100%",
          fontSize: 13,
        }}
      >
        <thead>
          <tr>
            {rows.length > 0 ? (
              rows.map((f, i) => (
                <th key={f} style={i === 0 ? s.thCorner : s.th}>
                  {FIELD_DEFS[f].label}
                </th>
              ))
            ) : (
              <th style={s.thCorner}></th>
            )}
            {displayColKeys.map((ck) =>
              vals.map((v) => (
                <th key={ck + v.field} style={s.th}>
                  {colHeaderLabel(ck, v, vals)}
                </th>
              )),
            )}
          </tr>
        </thead>
        <tbody>
          {rowKeys.map((rk) => {
            const sample = rowGroups[rk][0];
            return (
              <tr key={rk}>
                {rows.length > 0 ? (
                  rows.map((f, i) => (
                    <td key={f} style={i === 0 ? s.tdHead : s.td}>
                      {sample?.[f] ?? "-"}
                    </td>
                  ))
                ) : (
                  <td style={s.tdHead}>{rk}</td>
                )}
                {displayColKeys.map((ck) =>
                  vals.map((v) => (
                    <td key={ck + v.field} style={s.td}>
                      {fmt(
                        cellValue(rowGroups[rk], ck, v.field, v.agg),
                        v.field,
                        v.agg,
                      )}
                    </td>
                  )),
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td style={s.tfoot} colSpan={rowFieldCount}>
              Grand Total
            </td>
            {displayColKeys.map((ck) =>
              vals.map((v) => (
                <td key={ck + v.field} style={s.tfootValue}>
                  {fmt(cellValue(records, ck, v.field, v.agg), v.field, v.agg)}
                </td>
              )),
            )}
          </tr>
        </tfoot>
      </table>
    </Box>
  );
}

// ---- GROUPED view: real hierarchical pivot ----
// Leaf rows show one value per row-dimension field; non-leaf (group) rows
// render as a single bold, indented, clickable subtotal row spanning the
// remaining row-header columns, aggregated over just that group's records.
// Clicking a subtotal row toggles its children.
function GroupedPivotTable({
  rows,
  displayColKeys,
  cols,
  vals,
  flatRows,
  records,
  cellValue,
  collapsedKeys,
  onToggle,
}) {
  const rowFieldCount = Math.max(rows.length, 1);
  const s = useTableStyles();

  return (
    <Box
      sx={{
        overflowX: "scroll",
        overflowY: "auto",
        maxHeight: 520,
        width: "100%",
        minWidth: 0,
        overscrollBehaviorX: "contain",
        scrollbarGutter: "stable",
        pb: 1,
        "&::-webkit-scrollbar": { height: 8, width: 8 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#b8c0c8",
          borderRadius: 4,
        },
      }}
    >
      <table
        style={{
          borderCollapse: "separate",
          borderSpacing: 0,
          width: "max-content",
          minWidth: "100%",
          fontSize: 13,
        }}
      >
        <thead>
          <tr>
            {rows.length > 0 ? (
              rows.map((f, i) => (
                <th key={f} style={i === 0 ? s.thCorner : s.th}>
                  {FIELD_DEFS[f].label}
                </th>
              ))
            ) : (
              <th style={s.thCorner}></th>
            )}
            {displayColKeys.map((ck) =>
              vals.map((v) => (
                <th key={ck + v.field} style={s.th}>
                  {colHeaderLabel(ck, v, vals)}
                </th>
              )),
            )}
          </tr>
        </thead>
        <tbody>
          {flatRows.map((node) => {
            if (node.isLeaf) {
              const sample = node.records[0];
              return (
                <tr key={node.pathKey}>
                  {rows.map((f, i) => (
                    <td key={f} style={i === 0 ? s.tdHead : s.td}>
                      {sample?.[f] ?? "-"}
                    </td>
                  ))}
                  {displayColKeys.map((ck) =>
                    vals.map((v) => (
                      <td key={ck + v.field} style={s.td}>
                        {fmt(
                          cellValue(node.records, ck, v.field, v.agg),
                          v.field,
                          v.agg,
                        )}
                      </td>
                    )),
                  )}
                </tr>
              );
            }

            const collapsed = collapsedKeys.has(node.pathKey);
            return (
              <tr key={node.pathKey}>
                {node.depth > 0 && (
                  <td colSpan={node.depth} style={s.tdHead} />
                )}
                <td
                  colSpan={rowFieldCount - node.depth}
                  onClick={() => onToggle(node.pathKey)}
                  style={{
                    ...s.tdSubtotal,
                    paddingLeft: 12 + node.depth * 16,
                  }}
                >
                  <span style={{ display: "inline-block", width: 14 }}>
                    {collapsed ? "▸" : "▾"}
                  </span>
                  {FIELD_DEFS[node.field].label}: {node.value ?? "-"}
                </td>
                {displayColKeys.map((ck) =>
                  vals.map((v) => (
                    <td key={ck + v.field} style={s.tdSubtotalValue}>
                      {fmt(
                        cellValue(node.records, ck, v.field, v.agg),
                        v.field,
                        v.agg,
                      )}
                    </td>
                  )),
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td style={s.tfoot} colSpan={rowFieldCount}>
              Grand Total
            </td>
            {displayColKeys.map((ck) =>
              vals.map((v) => (
                <td key={ck + v.field} style={s.tfootValue}>
                  {fmt(cellValue(records, ck, v.field, v.agg), v.field, v.agg)}
                </td>
              )),
            )}
          </tr>
        </tfoot>
      </table>
    </Box>
  );
}

// ---- Region / Shop / Date → Top & Least seller table ----
function TopLeastTable({ rows }) {
  const s = useTableStyles();
  return (
    <Box
      sx={{
        overflow: "hidden",
        height: 400,
        maxHeight: 400,
        width: "100%",
        maxWidth: "76vw",
        minWidth: 0,
        position: "relative",
        display: "block",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflowX: "scroll",
          overflowY: "auto",
          overscrollBehavior: "contain",
          scrollbarGutter: "stable",
          pb: 1,
          "&::-webkit-scrollbar": { height: 8, width: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#b8c0c8",
            borderRadius: 4,
          },
        }}
      >
        <table
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            width: "max-content",
            minWidth: "max-content",
            maxWidth: "none",
            fontSize: 13,
          }}
        >
          <thead>
            <tr>
              {[
                "Region",
                "Shop",
                "Date",
                "Top Seller",
                "Top Qty",
                "Top Amount",
                "Least Seller",
                "Least Qty",
                "Least Amount",
              ].map((h, i) => (
                <th key={h} style={i === 0 ? s.thCorner : s.th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={s.tdHead}>{r.region}</td>
                <td style={s.td}>{r.shop}</td>
                <td style={s.td}>{r.date}</td>
                <td style={s.td}>{r.topSeller}</td>
                <td style={s.td}>{r.topQty}</td>
                <td style={s.td}>{fmt(r.topAmount, "total")}</td>
                <td style={s.td}>{r.leastSeller}</td>
                <td style={s.td}>{r.leastQty}</td>
                <td style={s.td}>{fmt(r.leastAmount, "total")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}

const thStyle = {
  textAlign: "center",
  padding: "8px 12px",
  boxShadow: "inset 0 -2px 0 #333",
  position: "sticky",
  top: 0,
  whiteSpace: "nowrap",
  zIndex: 2,
};
const thCornerStyle = {
  ...thStyle,
  left: 0,
  zIndex: 3,
};
const tdStyle = {
  textAlign: "center",
  padding: "6px 12px",
  whiteSpace: "nowrap",
};
const tdRowHead = {
  textAlign: "center",
  padding: "6px 12px",
  fontWeight: 500,
  position: "sticky",
  left: 0,
  zIndex: 1,
};
const tfootHead = {
  textAlign: "center",
  padding: "8px 12px",
  fontWeight: 700,
};
const tfootStyle = {
  textAlign: "center",
  padding: "8px 12px",
  fontWeight: 700,
};

// Returns theme-aware table style objects. Call inside any component that renders the table.
function useTableStyles() {
  const { palette } = useTheme();
  const isDark = palette.mode === "dark";

  const headerBg  = isDark ? "#2a2a2a" : "#f5f5f5";
  const headerBorder = isDark ? "#555" : "#333";
  const rowBg     = isDark ? palette.background.paper : "#fff";
  const rowBorder = isDark ? "#3a3a3a" : "#eee";
  const subtotalBg = isDark ? "#1e2d23" : "#eef3ef";
  const footerBg  = isDark ? "#2a2a2a" : "#f5f5f5";

  return {
    th: {
      ...thStyle,
      background: headerBg,
      color: palette.text.primary,
      boxShadow: `inset 0 -2px 0 ${headerBorder}`,
    },
    thCorner: {
      ...thCornerStyle,
      background: headerBg,
      color: palette.text.primary,
      boxShadow: `inset 0 -2px 0 ${headerBorder}`,
    },
    td: {
      ...tdStyle,
      color: palette.text.primary,
      boxShadow: `inset 0 -1px 0 ${rowBorder}`,
    },
    tdHead: {
      ...tdRowHead,
      background: rowBg,
      color: palette.text.primary,
      boxShadow: `inset 0 -1px 0 ${rowBorder}`,
    },
    tdSubtotal: {
      ...tdRowHead,
      background: subtotalBg,
      color: palette.text.primary,
      boxShadow: `inset 0 -1px 0 ${rowBorder}`,
      fontWeight: 700,
      cursor: "pointer",
    },
    tdSubtotalValue: {
      ...tdStyle,
      background: subtotalBg,
      color: palette.text.primary,
      fontWeight: 700,
      boxShadow: `inset 0 -1px 0 ${rowBorder}`,
    },
    tfoot: {
      ...tfootHead,
      background: footerBg,
      color: palette.text.primary,
      boxShadow: `inset 0 2px 0 ${headerBorder}`,
    },
    tfootValue: {
      ...tfootStyle,
      background: footerBg,
      color: palette.text.primary,
      boxShadow: `inset 0 2px 0 ${headerBorder}`,
    },
  };
}
